import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled, { withTheme } from 'styled-components';
import hexToRgba from 'hex-to-rgba';
import { CardContainer, Flex, FlexColumn } from '../../../Reusable/Container';
import { Text, Tag } from '../../../Reusable/Text';
import { LabeledInput } from '../../../Reusable/Input';
import LucideIcon, { TextButton, ImageButton, Button } from '../../../Reusable/Button';
import PageHeader from '../../../Reusable/PageHeader';

import { Skeleton } from 'antd';
import BillSummary from './Reusable/BillSummary';
import NewVendor from '../../Vendors/NewVendor';
import { LucideX } from 'lucide-react';
import EditBill from './Reusable/EditBill';
import ChangeVendor from './Reusable/ChangeVendor';
import EditExpenseCategorization from './Reusable/EditExpenseCategorization';

// Styled components for the bill detail panel
const DetailPanelContainer = styled(FlexColumn)`
  width: 100%;
  height: 100%;
  padding: 24px;
  background: ${props => props.theme.body};
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  position: relative;
  padding-bottom: 100px;
`;


class BillDetailPanel extends Component {
    state = {
        submitLoading: false,
        loading: this.props.loading,
        flowSteps: [
            "Summary",
            "Edit",
            "NewVendor",
            "Categorization"
        ],
        currentStep: "Summary",
        payMode: false,
        bill: this.props.bill,
        inboxItem: this.props.inboxItem,
        vendor: this.props.vendor,
        cardTitle: this.props.inboxItem ? "Create Bill" : "Bill Summary",
        bCrumbTitle: null,
        convertedBillInbox: false,
        expenseCategorizationRequired: false
    }

    componentDidUpdate(prevProps) {
        // Check if bill prop has changed
        if (this.props.bill !== prevProps.bill) {
            this.setState({ bill: this.props.bill });
        }

        // Check if inboxItem prop has changed
        if (this.props.inboxItem !== prevProps.inboxItem) {
            this.setState({
                inboxItem: this.props.inboxItem,
                // Also update cardTitle which depends on inboxItem
                cardTitle: this.props.inboxItem ? "New Bill" : "Bill Summary"
            });
        }

        // Check if vendor prop has changed
        if (this.props.vendor !== prevProps.vendor) {
            this.setState({ vendor: this.props.vendor });
        }

        // Check if loading prop has changed
        if (this.props.loading !== prevProps.loading) {
            this.setState({ loading: this.props.loading });
        }
    }

    updateBillVendor = (options) => {
        options = options || {};
        var { bill, vendor, inboxItem } = this.state;

        if (options.bill) bill = options.bill;
        if (options.vendor) vendor = options.vendor;

        bill.vendorName = vendor.vendorName;
        bill.vendorId = vendor.id;

        var updateState = { vendor, bill };
        if (options.loadStep) updateState.currentStep = options.loadStep;
        if (options.updateInboxItem) {
            updateState.updateInboxItem = true;
            inboxItem.vendorName = vendor.vendorName;
            inboxItem.vendorId = vendor.id;
            updateState.inboxItem = inboxItem;
            console.log("updateBillVendor", updateState);
        }

        this.setState(updateState);
        this.props.updateBillVendor(updateState);
    }

    render() {
        const { theme } = this.props;
        var { cardTitle, prevStep, loading, bCrumbTitle } = this.state;

        return (
            <DetailPanelContainer>
                <Flex centerH start between style={{ margin: '12px 0 24px 0' }}>
                    <FlexColumn left gap='8px'>
                        <Text color={hexToRgba(theme.colors.primary2, 0.6)}>
                            {!loading && bCrumbTitle}
                        </Text>
                        <Text size="24px" weight={700}>
                            {!loading && cardTitle}
                        </Text>
                    </FlexColumn>

                    {
                        prevStep ?
                            <a style={{ textDecoration: "underline", color: hexToRgba(theme.colors.primary2, 0.6), fontSize: '14px' }} onClick={() => this.cancelStep()}>Cancel</a>
                            :
                            null
                    }

                </Flex>

                <FlexColumn fullHeight={true} style={{ minHeight: '100%', overflowY: 'auto' }} start>
                    {this.getContentViews()}
                </FlexColumn>
            </DetailPanelContainer>
        );
    }

    getCardTitle = (step) => {
        var { bCrumbTitle, cardTitle } = this.state;
        switch (step) {
            case "NewVendor":
                bCrumbTitle = null;
                cardTitle = "New Vendor Setup";
                break;
            case "ChangeVendor":
                bCrumbTitle = "Edit Bill /"
                cardTitle = "Vendor Details";
                break;
            case "EditExpenseCategorization":
                bCrumbTitle = "Edit Bill /"
                cardTitle = "Expense Categorization";
                break;
            case "Edit":
                bCrumbTitle = null;
                cardTitle = "Edit Bill";
                break;
            case "Summary":
                cardTitle = this.props.inboxItem ? "New Bill" : "Bill Summary";
            default:
                break;
        }
        return { bCrumbTitle, cardTitle };
    }

    loadStep = (nextStep) => {
        var { currentStep, cardTitle, bCrumbTitle } = this.state;
        if(nextStep == "Summary") {
            currentStep = null;
        }
        this.setState({ prevStep: currentStep, currentStep: nextStep, cardTitle: this.getCardTitle(nextStep).cardTitle, bCrumbTitle: this.getCardTitle(nextStep).bCrumbTitle });
    }

    cancelStep = () => {
        var { prevStep } = this.state;
        this.setState({ prevStep: null, currentStep: prevStep, cardTitle: this.getCardTitle(prevStep).cardTitle, bCrumbTitle: this.getCardTitle(prevStep).bCrumbTitle });
    }

    // This will provide the view for each flowView
    getContentViews = () => {
        var { loading, bill, vendor, currentStep, inboxItem, submitLoading, convertedBillInbox, expenseCategorizationRequired } = this.state;
        var { vendorId } = bill || {};
        var { theme, scheduledPayments, viewMode } = this.props;

        if (loading) {
            return (
                <FlexColumn style={{ margin: '40px 0', width: '100%' }}>
                    <Skeleton loading={loading} active title={false} paragraph={{ rows: 10 }}></Skeleton>
                </FlexColumn>
            )
        }

        switch (currentStep) {
            case "Summary":
                return (
                    <FlexColumn gap='24px'>
                        <BillSummary
                            bill={bill}
                            vendor={vendor}
                            scheduledPayments={scheduledPayments}
                            getSchldPmts={this.props.getSchldPmts}
                            getBill={this.props.getBill}
                            billAmtPaid={this.props.billAmtPaid}
                            updateBillVendor={this.updateBillVendor}
                            viewMode={viewMode}
                            convertedBillInbox={convertedBillInbox}
                            loadStep={this.loadStep}
                            expenseCategorizationRequired={(required) => { this.setState({ expenseCategorizationRequired: required }) }}
                        />
                        <Flex start centerH gap='24px'>
                            {
                                inboxItem &&
                                (vendorId && !expenseCategorizationRequired &&
                                    <Button solid loading={submitLoading} onClick={() => {
                                        this.setState({ submitLoading: true });
                                        this.props.saveBill(bill, () => {
                                            // console.log("BillDetailPanel saveBill", bill);
                                            this.setState({ submitLoading: false, convertedBillInbox: true });
                                            this.props.getSchldPmts(bill?.id);
                                            this.props.toggleViewPayMode(bill);
                                        });
                                    }} text={!submitLoading ? "LOOKS GOOD!" : "SAVING TO DRAFTS"} />
                                )
                            }
                            <a style={{ textDecoration: "underline", color: hexToRgba(theme.colors.primary2, 0.6), fontSize: '16px' }} onClick={() => {
                                this.loadStep("Edit");
                                if(!viewMode) this.props.toggleViewPayMode(bill); // Toggle to turn on view mode
                            }}>Edit bill details</a>
                        </Flex>

                    </FlexColumn>
                );
            case "NewVendor":
                return (
                    <NewVendor 
                        billFlow 
                        vendor={vendor} 
                        onVendorSave={(vendor, loadStep) => {
                            if(loadStep) this.updateBillVendor({ loadStep: "Summary" });
                            else this.updateBillVendor({ vendor, updateInboxItem: true });
                        }} 
                        contentWidth='100%' 
                    />
                )
            case "Edit":
                return (
                    <EditBill
                        inboxItem={inboxItem}
                        bill={bill}
                        vendor={vendor}
                        updateBillVendor={this.updateBillVendor}
                        loadStep={this.loadStep}
                        cancelStep={this.cancelStep}
                        saveBill={this.props.saveBill}
                    />
                )
            case "ChangeVendor":
                return (
                    <ChangeVendor bill={bill} vendor={vendor} updateBillVendor={this.updateBillVendor} loadStep={this.loadStep} cancelStep={this.cancelStep} />
                )
            case "EditExpenseCategorization":
                return (
                    <EditExpenseCategorization
                        editMode
                        bill={bill}
                        vendor={vendor}
                        updateBillVendor={this.updateBillVendor}
                        loadStep={this.loadStep}
                        cancelStep={this.cancelStep}
                        showTotal={true}
                    />
                )
            default:
                break;
        }
    }

    loadPrevView = () => {
        if (this.state.currentViewIndex !== 0) {
            this.setState({
                currentViewIndex: this.state.currentViewIndex - 1,
                showError: false, errorMsg: ""      // dismiss any validatin error, if any, for the current step
            });
        }
    }
}


function mapStateToProps(state) {
    return {
        store: state.aionAppReducer,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTheme(BillDetailPanel)));